// export default () => <></>
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { GlobalContext } from '@providers/globalStore'
import { FormPartComponents } from '@models/common'
import { useTranslation } from 'react-i18next'
import useQuery from '@hooks/useQuery'
import { QueryStepParser } from '@utils/queryStepParser'
import { FormContext } from '@providers/formStateProvider'
import { NavigateTo } from '@utils/navigate'
import uuid from 'react-uuid'
import FormService from '@services/form.service'
import { FormEIModel, InitEIForm, EiGeneralOptions } from '@services/model/form/form.EI.model'
import { useNavigate, useParams } from 'react-router-dom'
import useFormSwitcher from '@hooks/useFormSwitcher'
import FormEIService from '@services/formService/form.EI.service'
import useAPIFetch from '@hooks/useAPIFetch'
import { useSnackbar } from 'notistack'
import { DevTool } from '@hookform/devtools'
import { NFGeneralOptions } from '@services/model/form/form.NF.model'
import FormNFService from '@services/formService/form.NF.service'
import useGeneralOptions from '@hooks/useGeneralOptions_to_be_used'
import { PartA } from './part-a'
import PartB from './part-b'
import PartC from './part-c'
import PartD from './part-d'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ValidationToast from '@components/form/ValidationToast'
import getFormValidationSchema from '@utils/formValidationSchema'
import { FormStatusEnum } from '@services/model/form/form.model'
import { canvasExport } from '@utils/canvasExport'
import { find, get } from 'lodash'
import useLocalStorage from '@hooks/useLocalStorage'

export default function EIForm() {
  const { state: globalState, userInfo, dispatch: globalAction } = useContext(GlobalContext)
  const { state: formState, dispatch: formStateAction } = useContext(FormContext)
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [storageContractId, setStorageContractId] = useLocalStorage<string | undefined>(
    'contract',
    undefined,
  )
  // extracting id from params
  let { id: formId } = useParams<string>()

  // extracting step from query
  const query = useQuery()
  const step = QueryStepParser(query.get('step'))

  const validationSchemaConditions = [
    {
      formStatusList: [FormStatusEnum.FORM_EI_DRAFT, FormStatusEnum.FORM_EI_REJECTED],
      validationSchema: yup.object().shape({
        baseForm: yup.object().shape({
          contractNoId: yup
            .number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .moreThan(0, t('Contract No. is required'))
            .required(t('Contract No. is required')),
          teamId: yup.array().required().min(1),
          districtId: yup.array().required().min(1),
        }),
        location: yup.object().required(t('Location is required')),
        roadName: yup.string().required(t('Roadname is required')),
        dateOfCheck: yup.date().required(t('Date of Checking is required')),
        formNf: yup
          .array()
          .of(
            yup.object().shape({
              location: yup.object().required(t('Location in Defect Identified is required')),
              roadName: yup.string().required(t('Roadname in Defect Identified is required')),
              scopeOfWorksId: yup
                .number()
                .required(t('Work Order No. in Defect Identified is required')),
            }),
          )
          .nullable(),
      }),
    },
    {
      formStatusList: [FormStatusEnum.FORM_EI_REVIEW],
      validationSchema: yup.object(),
    },
  ]

  //default value, set current date as check default value
  InitEIForm.dateOfCheck = new Date()

  const { control, watch, setValue, getValues, reset, trigger } = useForm<FormEIModel>({
    defaultValues: { ...InitEIForm },
    resolver: (data, context, options) => {
      const validatorSchema = getFormValidationSchema(
        data.baseForm.formStatus,
        validationSchemaConditions,
      )
      return yupResolver(validatorSchema)(data, context, options)
    },
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  })

  const [formStatus] = watch(['baseForm.formStatus'])

  const formPermission = getValues('baseForm.formPermission')
  const [eiGeneralOptions, setEiGeneralOptions] = useState<EiGeneralOptions>(
    globalState.formOptionsList?.find(({ key }) => key === 'EI')?.value ?? {},
  )
  const [nfGeneralOptions, setNfGeneralOptions] = useState<NFGeneralOptions>(
    globalState.formOptionsList?.find(({ key }) => key === 'NF')?.value ?? {},
  )
  const isMounted = useRef(false)

  const { setRequest, isLoading } = useAPIFetch()
  const { setRequest: setOptionRequest, isLoading: isLoadingOption } = useAPIFetch()

  useEffect(() => {
    if (!isMounted.current) {
      if (formState.form && formState.formType === 'EI') {
        formStateAction({ type: 'clear' })

        reset({ ...formState.form })
      } else if (formId) {
        reload()
      } else {
        setValue('baseForm.contractNoId', storageContractId ? parseInt(storageContractId) : 0)
        setValue('baseForm.teamId', userInfo.teams)
        setValue('baseForm.districtId', userInfo.districts)
      }

      globalAction({
        type: 'changeFormStatus',
        formStatus: getValues('baseForm.formStatusName'),
      })
      isMounted.current = true
    }

    return () => {
      // formStateAction({ type: 'clear' })
      globalAction({
        type: 'changeFormStatus',
        formStatus: undefined,
      })
    }
  }, [formId, formStateAction, globalAction])

  const reload = async () => {
    setRequest({
      callback: async () => {
        if (formId) {
          await FormEIService.GetEIForm(formId)
            .then(async (f) => {
              if (f) reset(f)

              if (f.locationMapBase64 === undefined) {
                setTimeout(() => {
                  saveTargetMap().then(async () => await FormEIService.UpdateMap(getValues()))
                }, 20000)
              }

              if (f.baseForm.contractNoId && f.baseForm.contractNoId !== globalState.contractNoId) {
                globalAction({
                  type: 'selectContract',
                  contractNoId: f.baseForm.contractNoId,
                })

                globalAction({
                  type: 'selectWorkGroup',
                  workGroupId: get(
                    find(globalState.contractList, { id: f.baseForm.contractNoId }),
                    'workGroups.0.id',
                    0,
                  ),
                })
              }

              globalAction({
                type: 'changeFormStatus',
                formStatus: f.baseForm.formStatusName,
              })
            })
            .catch((err) => {
              enqueueSnackbar(err.response.data.message, {
                variant: 'error',
                autoHideDuration: null,
              })
              NavigateTo(navigate, '/engineer-inspection/all-record')
            })
        }
      },
    })
  }

  const handleOnSubmit = async (
    event: any,
    isReject: boolean,
    notifyList?: string[],
    signatureBase64?: string,
    submissionComment?: string,
  ) => {
    if (!(await trigger())) return

    setValue('signatureBase64', signatureBase64)
    var ff = getValues()
    ff.signatureBase64 = signatureBase64
    ff.notifyUserList = notifyList
    ff.submissionComment = submissionComment

    if (isReject) {
      setRequest({
        callback: async () => {
          await FormEIService.RejectEIForm(ff).then(() => {
            enqueueSnackbar(t('Record Submitted'), { variant: 'success' })
            NavigateTo(navigate, '/engineer-inspection/all-record')
          })
        },
      })
    } else {
      setRequest({
        callback: async () => {
          await FormEIService.ApproveEIForm(ff).then(() => {
            enqueueSnackbar(t('Record Submitted'), { variant: 'success' })
            NavigateTo(navigate, '/engineer-inspection/all-record')
          })
        },
      })
    }
  }

  const handleOnSave = async () => {
    // TODO: on save
    setRequest({
      callback: async () => {
        await FormEIService.SaveEIForm(getValues()).then((resp) => {
          setValue('baseForm.formId', resp.formId)
          setValue('baseForm.formStatus', resp.formStatus)
          console.log('values', getValues())

          NavigateTo(navigate, '/engineer-inspection/:id', {
            id: resp.formId,
          })

          formId = resp.formId
          reload()

          enqueueSnackbar(t('Record Saved'), { variant: 'success' })
        })
      },
    })
  }

  const handleOnDelete = async () => {
    setRequest({
      callback: async () => {
        await FormEIService.DeleteEIForm(getValues())
          .then(() => {
            enqueueSnackbar(t('Record Archived'), { variant: 'success' })
            NavigateTo(navigate, '/engineer-inspection/all-record')
          })
          .catch((err) => {
            enqueueSnackbar(err.response.data.message, { variant: 'error', autoHideDuration: null })
          })
      },
    })
  }

  const mapRef = useRef<HTMLUListElement>(null)
  const saveTargetMap = async () => {
    let targetBase64 = await canvasExport(mapRef)
    setValue('locationMapBase64', targetBase64)
  }

  const EIPartA = (): FormPartComponents => {
    return {
      partTitle: t('General information'),
      component: (
        <PartA
          control={control}
          formStatus={formStatus}
          userInfo={userInfo}
          useWatch={useWatch}
          options={eiGeneralOptions}
          globalState={globalState}
          setValue={setValue}
          isMounted={isMounted}
          watch={watch}
          getValues={getValues}
          ref={mapRef}
        />
      ),
      onNext: () => saveTargetMap(),
    }
  }

  const EIPartB = (): FormPartComponents => {
    return {
      partTitle: t('Defects Identified'),
      component: (
        <PartB
          control={control}
          formStatus={formStatus}
          formStateAction={formStateAction}
          getValues={getValues}
          formId={formId}
          options={nfGeneralOptions}
        />
      ),
    }
  }

  const EIPartC = (): FormPartComponents => {
    return {
      partTitle: t('Site Condition & Other Information'),
      component: <PartC control={control} formStatus={formStatus} />,
    }
  }

  const EIPartD = (): FormPartComponents => {
    return {
      partTitle: 'Submission Confirmation',
      component: (
        <PartD
          control={control}
          getValues={getValues}
          globalState={globalState}
          formPermission={formPermission}
          handleOnComplete={() => handleOnComplete()}
        />
      ), //TODO: Endofflow, userMetaList
    }
  }

  const onLoadNotifyList = async (isRejcet: boolean) => {
    var defailtNotifyList = await FormService.GetNotifyList(
      'EI',
      getValues('baseForm.formId'),
      isRejcet,
      getValues('baseForm.districtId'),
      getValues('baseForm.teamId'),
    )
    return defailtNotifyList
  }

  const onLoadHistoryList = async () => {
    var defaultNotifyList = await FormService.GetHistoryList(getValues('baseForm.formId'))
    return defaultNotifyList
  }

  const [FormSwitcher, handleOnComplete] = useFormSwitcher({
    title: t('ENGINEER INSPECTION'),
    components: [EIPartA(), EIPartB(), EIPartC(), EIPartD()],
    formOnLoadNotifyList: onLoadNotifyList,
    formOnLoadCommentList: async () => {
      return await FormService.GetCommentList(getValues('baseForm.formId'))
    },
    formOnSubmitComment: async (comment) => {
      await FormService.SubmitComment(getValues('baseForm.formId'), comment)
    },
    formOnLoadFormHistoryList: onLoadHistoryList,
    formOnSubmit: handleOnSubmit,
    formOnDelete: handleOnDelete,
    formOnSave: handleOnSave,
    startStep: step,
    isLoading: isLoading || isLoadingOption,
    disableSave: !formPermission?.canUpdate, // can edit this form
    disableDelete: !formPermission?.canDelete, // can delete this form
    disableComment: getValues('baseForm.formId') ? false : true,
    approveRequired: formPermission?.workflowRequired, // can go to next stage
    endOfFlow: formPermission?.endOfFlow,
    isMounted: isMounted.current,
  })

  return (
    <Fragment>
      <FormSwitcher />
      <DevTool control={control} />
      <ValidationToast control={control} />
    </Fragment>
  )
}
